import { useEffect, useState } from 'react';
import { CabeceraPagina } from '../../components/Armazon';
import { Aviso, Boton, Campo, Cargando, Tarjeta } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAvisos, useDatos } from '../../lib/sesion';

/* ═══════════════════════════ Mejora M-07 ═══════════════════════════════════
   Configuración del servidor de correo desde Administración. Antes solo se
   podía escribir una dirección de destino; ahora se configura el servidor
   entero y se puede probar sin salir de la pantalla.

   Dos cautelas:
     · La contraseña se escribe pero no se lee. La API nunca la devuelve.
     · Si el despliegue define SMTP_HOST en el entorno, manda el entorno y la
       pantalla se muestra bloqueada. Producción no se desconfigura desde el
       navegador.
   ═════════════════════════════════════════════════════════════════════════ */

interface Config {
  values: Record<string, string>;
  secretsSet: Record<string, boolean>;
  descriptions: Record<string, string>;
  defaults: Record<string, string>;
  integrations: { googleMode: string; mailMode: string; timezone: string };
  correo: {
    modoEfectivo: 'smtp' | 'fake';
    origen: 'entorno' | 'configuracion' | 'ninguno';
    motivo: string;
    bloqueadoPorEntorno: boolean;
    host: string;
    from: string;
  };
}

const K = {
  salvador: 'mail.salvador',
  jefe: 'mail.sales_manager',
  host: 'mail.smtp_host',
  puerto: 'mail.smtp_port',
  seguridad: 'mail.smtp_security',
  usuario: 'mail.smtp_user',
  password: 'mail.smtp_password',
  from: 'mail.from',
  zona: 'app.timezone',
  paso: 'agenda.step_minutes',
  duracion: 'agenda.default_duration_minutes',
  calendario: 'google.master_calendar_id',
  politica: 'google.missing_event_policy',
  rotuloGrupo: 'ui.client_group_label',
};

const ROTULOS: Record<string, string> = {
  'import.excluded_salesperson_ids': 'Ids. Vendedor excluidos (separados por comas)',
  'stats.last_minute_hours': 'Último momento: horas antes del inicio',
  'review.start_date': 'Fecha de inicio de valoraciones',
  'review.reminder_first_hours': 'Primer aviso (horas)',
  'review.reminder_every_days': 'Valoración: intervalo (días)',
  'review.followup_every_days': 'Seguimiento: intervalo (días)',
  'review.followup_close_days': 'Cierre desde la visita (días)',
  'review.escalate_after': 'Escalar al Jefe tras N avisos (0 = nunca)',

  [K.salvador]: 'Correo de Salvador Pinar',
  [K.jefe]: 'Correo del Jefe de Ventas',
  [K.host]: 'Servidor SMTP',
  [K.puerto]: 'Puerto',
  [K.seguridad]: 'Seguridad',
  [K.usuario]: 'Usuario',
  [K.password]: 'Contraseña',
  [K.from]: 'Remitente (From)',
  [K.zona]: 'Zona horaria',
  [K.paso]: 'Incremento de la agenda (minutos)',
  [K.duracion]: 'Duración propuesta por defecto (minutos)',
  [K.calendario]: 'Calendario maestro de Google',
  [K.politica]: 'Si un evento se borra en Google',
  [K.rotuloGrupo]: 'Rótulo de «descripción de grupo»',
};

export function Configuracion() {
  const { avisar } = useAvisos();
  const { datos, cargando, error, recargar } = useDatos(
    (s) => api.get<Config>('/admin/settings', s),
    [],
  );
  const [form, setForm] = useState<Record<string, string>>({});
  const [fallo, setFallo] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const [destinoPrueba, setDestinoPrueba] = useState('');
  const [probando, setProbando] = useState(false);
  const [resultado, setResultado] = useState<{ ok: boolean; mensaje: string } | null>(null);

  useEffect(() => {
    if (datos) setForm(datos.values);
  }, [datos]);

  if (cargando) return <Cargando />;
  if (error) return <Aviso tono="error">{error}</Aviso>;
  if (!datos) return null;

  const bloqueado = datos.correo.bloqueadoPorEntorno;

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const texto = (k: string, extra?: { tipo?: string; ayuda?: string; deshabilitado?: boolean }) => (
    <Campo
      key={k}
      etiqueta={ROTULOS[k] ?? k}
      ayuda={extra?.ayuda ?? datos.descriptions[k]}
    >
      <input
        type={extra?.tipo ?? 'text'}
        value={extra?.tipo === 'date' ? (form[k] ?? '').slice(0, 10) : (form[k] ?? '')}
        placeholder={datos.defaults[k]}
        disabled={extra?.deshabilitado}
        onChange={(e) => set(k, e.target.value)}
      />
    </Campo>
  );

  async function guardar() {
    setGuardando(true);
    setFallo(null);
    try {
      await api.put('/admin/settings', form);
      avisar('Configuración guardada.', 'hecho');
      setResultado(null);
      recargar();
    } catch (e) {
      setFallo(e instanceof ApiError ? e.message : 'No se ha podido guardar.');
    } finally {
      setGuardando(false);
    }
  }

  async function probar() {
    setProbando(true);
    setResultado(null);
    try {
      const r = await api.post<{ ok: boolean; mensaje: string }>('/admin/settings/mail-test', {
        to: destinoPrueba.trim(),
      });
      setResultado(r);
    } catch (e) {
      setResultado({
        ok: false,
        mensaje: e instanceof ApiError ? e.message : 'No se ha podido lanzar la prueba.',
      });
    } finally {
      setProbando(false);
    }
  }

  return (
    <>
      <CabeceraPagina
        titulo="Configuración"
        acciones={
          <Boton variante="clave" cargando={guardando} onClick={guardar}>
            Guardar configuración
          </Boton>
        }
      />

      <div className="pila">
        {fallo && <Aviso tono="error">{fallo}</Aviso>}

        <Aviso tono={datos.correo.modoEfectivo === 'smtp' ? 'hecho' : 'info'}>
          Correo:{' '}
          <strong>
            {datos.correo.modoEfectivo === 'smtp' ? 'envío real' : 'simulado, no se envía nada'}
          </strong>{' '}
          · {datos.correo.motivo} — Google Calendar:{' '}
          <strong>{datos.integrations.googleMode === 'real' ? 'conectado' : 'simulado'}</strong> ·
          Zona horaria: <strong>{datos.integrations.timezone}</strong>
        </Aviso>

        {/* ── Servidor de correo ─────────────────────────────────────────── */}
        <Tarjeta titulo="Servidor de correo (SMTP)">
          <div className="pila">
            {bloqueado && (
              <Aviso tono="aviso">
                Este servidor está configurado mediante variables de entorno
                (<code>SMTP_HOST</code>), que tienen prioridad sobre lo que se escriba aquí. Para
                cambiarlo, edite el archivo <code>.env</code> del servidor y reinicie. Los campos
                aparecen bloqueados a propósito.
              </Aviso>
            )}

            <div className="rejilla dos">
              {texto(K.host, {
                deshabilitado: bloqueado,
                ayuda: 'Por ejemplo smtp.dominio.com. Déjelo vacío para no enviar correo.',
              })}
              {texto(K.puerto, { tipo: 'number', deshabilitado: bloqueado })}
            </div>

            <div className="rejilla dos">
              <Campo
                etiqueta={ROTULOS[K.seguridad]!}
                ayuda="587 suele ir con STARTTLS; 465 con SSL/TLS."
              >
                <select
                  value={form[K.seguridad] ?? 'starttls'}
                  disabled={bloqueado}
                  onChange={(e) => set(K.seguridad, e.target.value)}
                >
                  <option value="starttls">STARTTLS (recomendado)</option>
                  <option value="ssl">SSL/TLS</option>
                  <option value="none">Sin cifrado</option>
                </select>
              </Campo>
              {texto(K.usuario, { deshabilitado: bloqueado })}
            </div>

            <div className="rejilla dos">
              <Campo
                etiqueta={ROTULOS[K.password]!}
                ayuda={
                  datos.secretsSet[K.password]
                    ? 'Hay una contraseña guardada. Déjelo vacío para conservarla.'
                    : 'Se guarda cifrada y no vuelve a mostrarse.'
                }
              >
                <input
                  type="password"
                  autoComplete="new-password"
                  value={form[K.password] ?? ''}
                  placeholder={datos.secretsSet[K.password] ? '•••••••• (sin cambios)' : ''}
                  disabled={bloqueado}
                  onChange={(e) => set(K.password, e.target.value)}
                />
              </Campo>
              {texto(K.from, {
                deshabilitado: bloqueado,
                ayuda: 'Ejemplo: Salvador Pinar <visitas@dominio.com>',
              })}
            </div>
          </div>
        </Tarjeta>

        {/* ── Prueba ─────────────────────────────────────────────────────── */}
        <Tarjeta titulo="Probar el envío">
          <div className="pila">
            <p className="sutil" style={{ margin: 0 }}>
              Guarde antes los cambios. Se envía un correo real.
            </p>
            <div className="fila" style={{ alignItems: 'flex-end' }}>
              <Campo etiqueta="Enviar un correo de prueba a">
                <input
                  type="email"
                  value={destinoPrueba}
                  placeholder="usted@dominio.com"
                  onChange={(e) => setDestinoPrueba(e.target.value)}
                  style={{ minWidth: 260 }}
                />
              </Campo>
              <Boton
                variante="secundario"
                cargando={probando}
                disabled={!destinoPrueba.includes('@')}
                onClick={probar}
              >
                Enviar prueba
              </Boton>
            </div>
            {resultado && (
              <Aviso tono={resultado.ok ? 'hecho' : 'error'}>
                {resultado.ok ? (
                  resultado.mensaje
                ) : (
                  <>
                    <strong>La prueba ha fallado.</strong>
                    <br />
                    <code style={{ fontSize: 13 }}>{resultado.mensaje}</code>
                  </>
                )}
              </Aviso>
            )}
          </div>
        </Tarjeta>

        {/* ── Destinatarios ──────────────────────────────────────────────── */}
        <Tarjeta titulo="Destinatarios de los avisos">
          <div className="pila">
            <p className="sutil" style={{ margin: 0 }}>
              Comercial y Cocinero reciben los avisos en el correo de su ficha.
            </p>
            <div className="rejilla dos">
              {texto(K.jefe, { tipo: 'email' })}
              {texto(K.salvador, { tipo: 'email' })}
            </div>
          </div>
        </Tarjeta>

        <Tarjeta titulo="Importación">{texto('import.excluded_salesperson_ids')}</Tarjeta>
        <Tarjeta titulo="Estadísticas">{texto('stats.last_minute_hours',{tipo:'number'})}</Tarjeta>
        <Tarjeta titulo="Valoración y recordatorios">
          <div className="rejilla dos">
            {texto('review.start_date', { tipo: 'date', ayuda: 'Solo las visitas terminadas a partir de esta fecha entran en valoración' })}
            {['review.reminder_first_hours','review.reminder_every_days','review.followup_every_days','review.followup_close_days','review.escalate_after'].map(k=>texto(k,{tipo:'number'}))}
          </div>
        </Tarjeta>

        {/* ── Agenda ─────────────────────────────────────────────────────── */}
        <Tarjeta titulo="Agenda">
          <div className="rejilla tres">
            {texto(K.zona)}
            {texto(K.paso, { tipo: 'number' })}
            {texto(K.duracion, { tipo: 'number' })}
          </div>
        </Tarjeta>

        {/* ── Google Calendar ────────────────────────────────────────────── */}
        <Tarjeta titulo="Google Calendar">
          <div className="pila">
            {texto(K.calendario)}
            <Campo etiqueta={ROTULOS[K.politica]!} ayuda={datos.descriptions[K.politica]}>
              <select
                value={form[K.politica] ?? 'RECREATE'}
                onChange={(e) => set(K.politica, e.target.value)}
              >
                <option value="RECREATE">Volver a crearlo</option>
                <option value="INCIDENT">Solo registrar la incidencia</option>
              </select>
            </Campo>
          </div>
        </Tarjeta>

        {/* ── Interfaz ───────────────────────────────────────────────────── */}
        <Tarjeta titulo="Interfaz">{texto(K.rotuloGrupo)}</Tarjeta>

        <div className="barra-acciones">
          <Boton variante="clave" cargando={guardando} onClick={guardar}>
            Guardar configuración
          </Boton>
        </div>
      </div>
    </>
  );
}
